import Phaser from 'phaser';
import { Z } from './zOrder';
import { regions } from './layout';
import { makeTwoPassText, type TwoPassText } from './twoPassText';
import type { PuppyGame } from '../game/PuppyGame';

// Renders the bottom region: grass + flowers band with the park name and
// level title overlaid, and the info (?) button in the corner.
export class FooterView {
  readonly container: Phaser.GameObjects.Container;
  private band: Phaser.GameObjects.Image;
  private parkText: TwoPassText;
  private levelText: TwoPassText;
  private infoButtonCircle: Phaser.GameObjects.Image;
  private infoButton: TwoPassText;

  constructor(
    scene: Phaser.Scene,
    private game: PuppyGame,
    private parkNames: string[],
    onInfoTapped: () => void,
  ) {
    const r = regions().footer;
    this.container = scene.add.container(0, 0).setDepth(Z.bottomBar4);

    this.band = scene.add
      .image(r.x, r.y, 'bottombar')
      .setOrigin(0, 0)
      .setDisplaySize(r.width, r.height)
      .setDepth(Z.grassAndHills);

    // Park name + level title — stacked and centered vertically in the
    // footer band. Each uses the two-pass Snacker Comic styling.
    const parkFontPx = 56;
    const levelFontPx = 44;
    const gapPx = 6;
    const blockH = parkFontPx + gapPx + levelFontPx;
    const blockTop = r.y + (r.height - blockH) / 2;
    this.parkText = makeTwoPassText(scene, r.x + r.width / 2, blockTop, '', {
      fontSize: `${parkFontPx}px`,
      originX: 0.5,
      originY: 0,
      depth: Z.headerLevelLabels,
    });
    this.levelText = makeTwoPassText(
      scene,
      r.x + r.width / 2,
      blockTop + parkFontPx + gapPx,
      '',
      { fontSize: `${levelFontPx}px`, originX: 0.5, originY: 0, depth: Z.headerLevelLabels },
    );

    // "?" info button — 3× the previous size, sitting on a crazycountcircle
    // badge so it reads as a tappable bubble. Centred in the bottom-left.
    const infoFontPx = 64;
    const infoCircleSize = 76;
    const infoCx = r.x + 78;
    const infoCy = r.y + r.height - 78;
    this.infoButtonCircle = scene.add
      .image(infoCx, infoCy, 'crazycountcircle')
      .setDisplaySize(infoCircleSize, infoCircleSize)
      .setOrigin(0.5)
      .setDepth(Z.headerLevelLabels);
    this.infoButton = makeTwoPassText(scene, infoCx, infoCy, '?', {
      fontSize: `${infoFontPx}px`,
      depth: Z.headerLevelLabels + 1,
    });
    this.infoButton.fill.setInteractive({ useHandCursor: true });
    this.infoButton.fill.on('pointerdown', onInfoTapped);

    this.container.add([
      this.band,
      this.parkText.outline, this.parkText.fill,
      this.levelText.outline, this.levelText.fill,
      this.infoButtonCircle,
      this.infoButton.outline, this.infoButton.fill,
    ]);
  }

  refresh(): void {
    const idx =
      this.parkNames.length === 0
        ? 0
        : (this.game.levelNumber - 1) % this.parkNames.length;
    const name = this.parkNames[idx] ?? '';
    this.parkText.setText(name);
    this.levelText.setText(`Level ${this.game.levelNumber}`);
  }
}
